import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { REGIONS, DEPLOY_LOG } from './data';

// HOME — terminal command-line hero with blinking cursor + live region status grid.
// NO gradient hero, NO product preview-glow card. This is a cold, mono, ops console.
export default function Home({ setPage }) {
  const [cursor, setCursor] = useState(true);
  const [typed, setTyped] = useState('');
  const goal = 'mercurial deploy --region us-east-1 --watch';
  useEffect(() => {
    const id = setInterval(() => setCursor(c => !c), 520);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i++;
      if (i > goal.length) clearInterval(id);
      else setTyped(goal.slice(0, i));
    }, 38);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="bg-[#0A0E14] text-[#E6EDF3] font-mono">
      {/* TERMINAL HERO */}
      <section className="pt-32 pb-12 max-w-[1500px] mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* LEFT — terminal */}
          <div className="bg-[#0E141C] border border-[#1A2230] rounded-sm overflow-hidden shadow-[0_0_60px_rgba(6,214,160,0.05)]">
            <div className="px-5 py-2.5 border-b border-[#1A2230] flex items-center gap-2 text-[10px] text-[#5F7184]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]"></span>
              <span className="ml-2">~/mercurial — production console</span>
              <span className="ml-auto text-[#06D6A0]">● 18 regions · all green</span>
            </div>
            <div className="p-6 lg:p-9 text-sm">
              <p className="text-[#5F7184]">// platform · v8.4.2 · auth as eleanor.gault@acme</p>
              <p className="text-[#5F7184]">// last login: 2026-02-09 09:38:14 UTC</p>
              <p className="mt-3 text-[#5F7184]">// Ship code to 18 regions. Page on what matters.</p>
              <div className="mt-7 text-[#06D6A0]">
                <span className="text-[#5F7184]">eleanor@platform</span>
                <span className="text-[#5F7184]"> in </span>
                <span className="text-[#FFB454]">~/main </span>
                <span className="text-[#5F7184]">$</span> <span className="text-[#E6EDF3]">{typed}<span className={cursor ? 'text-[#06D6A0]' : 'text-transparent'}>▍</span></span>
              </div>

              <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }} className="font-sans text-4xl lg:text-7xl text-[#E6EDF3] mt-12 leading-[0.98] tracking-[-0.025em] lowercase font-medium">
                ship code.<br />
                <span className="text-[#06D6A0]">queue jobs.</span><br />
                <span className="text-[#5F7184]">page on what matters.</span>
              </motion.h1>
              <p className="font-sans text-[#5F7184] text-base mt-6 max-w-lg leading-[1.7]">A live-ops platform for engineering teams running production at scale. Edge runtime, durable queues, secret vault, and observability — in 18 regions, behind one CLI.</p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <button onClick={() => setPage('pricing')} data-testid="mc-home-cta" className="bg-[#06D6A0] text-[#0A0E14] px-5 py-2.5 text-sm hover:bg-[#5BFFC9] transition-colors">$ npm i -g mercurial</button>
                <button onClick={() => setPage('modules')} className="border border-[#5F7184]/40 text-[#E6EDF3] px-5 py-2.5 text-sm hover:border-[#06D6A0] hover:text-[#06D6A0] transition-colors">$ ls modules</button>
              </div>
            </div>
          </div>

          {/* RIGHT — region status pills */}
          <div className="bg-[#0E141C] border border-[#1A2230] rounded-sm">
            <div className="px-5 py-2.5 border-b border-[#1A2230] text-[10px] text-[#5F7184]">// regions · live state</div>
            <ul className="p-5 space-y-3 text-xs">
              {REGIONS.map(r => (
                <li key={r.code} className="flex items-center justify-between gap-3 border-b border-[#1A2230] last:border-0 pb-2.5 last:pb-0">
                  <div>
                    <p className="text-[#E6EDF3]">{r.code}</p>
                    <p className="text-[10px] text-[#5F7184]">{r.city}</p>
                  </div>
                  <p className="text-[#5F7184]">{r.latency}</p>
                  <span className={`px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase rounded-sm ${r.state === 'OPERATIONAL' ? 'bg-[#06D6A0]/10 text-[#06D6A0] border border-[#06D6A0]/30' : 'bg-[#FFB454]/10 text-[#FFB454] border border-[#FFB454]/30'}`}>{r.state}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DEPLOY LOG STREAM RIBBON */}
      <section className="border-y border-[#1A2230] bg-[#0E141C]">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-8 py-3 flex items-center gap-6 overflow-x-auto text-xs whitespace-nowrap">
          <span className="text-[10px] tracking-[0.32em] text-[#06D6A0] uppercase shrink-0">live deploy stream ●</span>
          {DEPLOY_LOG.map((l, i) => (
            <span key={i} className="flex items-center gap-2 shrink-0">
              <span className="text-[#5F7184]">{l.t}</span>
              <span className={l.sev === 'WARN' ? 'text-[#FFB454]' : 'text-[#06D6A0]'}>[{l.sev}]</span>
              <span className="text-[#E6EDF3]/85">{l.msg}</span>
              <span className="text-[#1A2230] mx-2">|</span>
            </span>
          ))}
        </div>
      </section>

      {/* TRUSTED BY — strip of pseudo-engineering teams */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-8 py-14">
        <p className="text-[10px] tracking-[0.32em] text-[#5F7184] uppercase mb-6">// trusted by 240+ engineering teams</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-10 gap-y-6 text-[#5F7184]">
          {['stripe-fwd','linear.app','railway','supabase','prisma','plain.com','arc.dev'].map(b => (
            <span key={b} className="text-base lowercase tracking-tight">{b}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
